import React from "react";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UUID from "../../../Utils/UUID";

const factoryUUID = new UUID();

/**
 * Affiche une liste déroulante générique avec libellés et options configurables.
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {boolean} [props.allowEmpty]
 * @param {string} [props.defaultValue]
 * @param {(id: string, value: string) => void} [props.onValueChanged]
 * @param {boolean} [props.isDarkStyle]
 * @param {string[]} [props.options]
 * @param {boolean} [props.isHashMapOption]
 * @param {boolean} [props.showIcon]
 * @param {Record<string, import('@fortawesome/fontawesome-svg-core').IconProp> | null} [props.icons]
 * @param {string} [props.optionSeparator]
 * @param {object} [props.I18n]
 * @param {(key: string) => string} props.I18n.get
 */
export default function AppSelect(props) {
    const {
        id,
        label,
        allowEmpty = false,
        defaultValue = "",
        onValueChanged,
        isDarkStyle = false,
        options = [],
        isHashMapOption = false,
        showIcon = false,
        icons = null,
        optionSeparator = "|",
        I18n
    } = props;

    const getInitialValue = () => {
        if (defaultValue && options && options.includes(defaultValue)) {
            return defaultValue;
        }
        return allowEmpty ? "" : "";
    };

    const [value, setValue] = React.useState(getInitialValue());

    const handleChange = (evt) => {
        const newValue = evt.target.value;
        setValue(newValue);

        if (onValueChanged) {
            onValueChanged(id, newValue);
        }
    };

    const renderMenuItem = (optionValue) => {
        if (optionValue && optionValue !== "") {
            let optValue = optionValue;
            let optLabel = I18n ? I18n.get(optionValue) : optionValue;

            if (isHashMapOption) {
                const parts = optionValue.split(optionSeparator);
                optValue = parts[0];
                optLabel = parts[1];
            }

            if (
                showIcon &&
                icons &&
                Object.prototype.hasOwnProperty.call(icons, optionValue)
            ) {
                return (
                    <MenuItem
                        key={`menu-${id}-item-${optionValue}-${factoryUUID.generate() || optionValue}`}
                        value={optValue}
                    >
                        <FontAwesomeIcon icon={icons[optValue]} style={{ fontSize: "1.25em" }} />
                        <span className="ml-2">{optLabel}</span>
                    </MenuItem>
                );
            }

            return (
                <MenuItem
                    key={`menu-${id}-item-${optionValue}-${factoryUUID.generate() || optionValue}`}
                    value={optValue}
                >
                    {optLabel}
                </MenuItem>
            );
        }

        return (
            <MenuItem key={`menu-${id}-item-none`} value="">
                <em>{I18n ? I18n.get("None") : "None"}</em>
            </MenuItem>
        );
    };

    React.useEffect(() => {
        let currentValue = defaultValue || "";

        if (isHashMapOption && currentValue) {
            currentValue = currentValue.split(optionSeparator)[0];
        }

        setValue(currentValue);
    }, [defaultValue, isHashMapOption, optionSeparator]);

    const renderedOptions = [];

    if (allowEmpty) {
        renderedOptions.push(renderMenuItem(null));
    }

    options.forEach((opt) => {
        renderedOptions.push(renderMenuItem(opt));
    });

    let css = "app-select-properties";
    let cssSelect = "app-select-combo-properties";
    let cssLabel = "app-select-label-properties";

    if (isDarkStyle) {
        css += " dark";
        cssSelect += " dark";
        cssLabel += " dark";
    }

    const currentValue = isHashMapOption && value
        ? value.split(optionSeparator)[0]
        : value;

    return (
        <FormControl variant="outlined" className={css}>
            <InputLabel className={cssLabel} id={`input-combo-${id}`}>
                {label}
            </InputLabel>

            <Select
                classes={{ selectMenu: cssSelect }}
                labelId={`app-select-${id}-label`}
                id={`app-select-${id}-id`}
                value={currentValue}
                onChange={handleChange}
                MenuProps={{ classes: { paper: cssSelect } }}
                label={label}
            >
                {renderedOptions}
            </Select>
        </FormControl>
    );
}
